import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// Register fonts for better typography - with error handling
try {
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2', fontWeight: 'normal' },
      { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBBc4.woff2', fontWeight: 'bold' },
    ]
  });
} catch (error) {
  console.warn('Font registration failed, using default fonts:', error);
}

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
  
  // Header Section
  header: {
    flexDirection: 'row',
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: '#f59e0b',
    borderBottomStyle: 'solid',
  },
  
  companySection: {
    flex: 2,
  },
  
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#d97706',
    marginBottom: 5,
  },
  
  companyDetails: {
    fontSize: 9,
    color: '#6b7280',
    lineHeight: 1.4,
  },
  
  invoiceSection: {
    flex: 1,
    alignItems: 'flex-end',
  },
  
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc2626',
    marginBottom: 10,
  },
  
  invoiceNumber: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 3,
  },
  
  invoiceDate: {
    fontSize: 10,
    color: '#6b7280',
  },
  
  // Customer Section
  customerSection: {
    flexDirection: 'row',
    marginBottom: 20,
    backgroundColor: '#fef3c7',
    padding: 15,
    borderRadius: 8,
  },
  
  billToSection: {
    flex: 1,
  },
  
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#d97706',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  
  customerName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 5,
  },
  
  customerDetails: {
    fontSize: 9,
    color: '#4b5563',
    lineHeight: 1.4,
  },
  
  // Items Table
  table: {
    marginBottom: 20,
  },
  
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f59e0b',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  
  tableHeaderText: {
    color: '#ffffff',
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    borderBottomStyle: 'solid',
    paddingVertical: 8,
    paddingHorizontal: 5,
    minHeight: 30,
  },
  
  tableRowAlt: {
    backgroundColor: '#fef3c7',
  },
  
  tableCell: {
    fontSize: 9,
    color: '#374151',
    textAlign: 'center',
    paddingHorizontal: 2,
  },
  
  tableCellLeft: {
    textAlign: 'left',
  },
  
  tableCellRight: {
    textAlign: 'right',
  },
  
  // Column widths for rent invoice
  col1: { width: '4%' },   // S.No
  col2: { width: '28%' },  // Item Name
  col3: { width: '11%' },  // From Date
  col4: { width: '11%' },  // To Date
  col5: { width: '7%' },   // Days
  col6: { width: '8%' },   // Qty
  col7: { width: '13%' },  // Rate
  col8: { width: '18%' },  // Amount
  
  // Summary Section
  summarySection: {
    flexDirection: 'row',
    marginTop: 10,
  },
  
  summaryLeft: {
    flex: 1,
  },
  
  summaryRight: {
    width: 200,
    backgroundColor: '#fef3c7',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fbbf24',
    borderStyle: 'solid',
  },
  
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  
  summaryLabel: {
    fontSize: 9,
    color: '#6b7280',
  },
  
  summaryValue: {
    fontSize: 9,
    color: '#374151',
    fontWeight: 'bold',
  },
  
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#fbbf24',
    borderTopStyle: 'solid',
  },
  
  totalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#d97706',
  },
  
  totalValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#dc2626',
  },
  
  // Payment Info
  paymentSection: {
    marginTop: 15,
    backgroundColor: '#dbeafe',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#3b82f6',
    borderLeftStyle: 'solid',
  },
  
  paymentTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 5,
  },
  
  paymentDetails: {
    fontSize: 9,
    color: '#1e3a8a',
    lineHeight: 1.4,
  },
  
  // Footer
  footer: {
    marginTop: 30,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    borderTopStyle: 'solid',
  },
  
  footerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  
  footerColumn: {
    flex: 1,
    paddingHorizontal: 10,
  },
  
  footerTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 5,
  },
  
  footerText: {
    fontSize: 8,
    color: '#6b7280',
    lineHeight: 1.3,
  },
  
  signature: {
    marginTop: 20,
    alignItems: 'flex-end',
  },
  
  signatureText: {
    fontSize: 9,
    color: '#6b7280',
    marginBottom: 30,
  },
  
  signatureLine: {
    width: 150,
    borderBottomWidth: 1,
    borderBottomColor: '#9ca3af',
    borderBottomStyle: 'solid',
    marginBottom: 5,
  },
  
  signatureLabel: {
    fontSize: 8,
    color: '#6b7280',
    textAlign: 'center',
  },
  
  // Watermark
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    fontSize: 35,
    color: '#fef3c7',
    opacity: 0.1,
    zIndex: -1,
    letterSpacing: 3,
  },
  
  // Status Badge
  statusBadge: {
    position: 'absolute',
    top: 100,
    right: 30,
    backgroundColor: '#10b981',
    color: '#ffffff',
    padding: 8,
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    transform: 'rotate(15deg)',
  },
  
  statusBadgePending: {
    backgroundColor: '#f59e0b',
  },
  
  statusBadgeOverdue: {
    backgroundColor: '#ef4444',
  },
});

const RentInvoicePDF = ({ rentInvoiceData, rentInvoiceDetails, companyData }) => {
  // Data validation and sanitization
  const safeRentData = {
    BillNumber: rentInvoiceData?.BillNumber || '0001',
    BillDate: rentInvoiceData?.BillDate || new Date().toISOString(),
    CustomerName: rentInvoiceData?.CustomerName || 'Customer Name',
    AddressLine1: rentInvoiceData?.AddressLine1 || '',
    AddressLine2: rentInvoiceData?.AddressLine2 || '',
    Location: rentInvoiceData?.Location || '',
    PinCode: rentInvoiceData?.PinCode || '',
    ContactNumber1: rentInvoiceData?.ContactNumber1 || '',
    ContactNumber2: rentInvoiceData?.ContactNumber2 || '',
    TotalAmount: parseFloat(rentInvoiceData?.TotalAmount) || 0,
    PaidAmount: parseFloat(rentInvoiceData?.PaidAmount) || 0,
    AdvanceAmount: parseFloat(rentInvoiceData?.AdvanceAmount) || 0,
    BalanceAmount: parseFloat(rentInvoiceData?.BalanceAmount) || 0,
    Remarks: rentInvoiceData?.Remarks || '',
    DeliveryOutDate: rentInvoiceData?.DeliveryOutDate || '',
    DeliveryInCreatedDate: rentInvoiceData?.DeliveryInCreatedDate || '',
    EventDays: parseFloat(rentInvoiceData?.EventDays) || 0,
  };

  const safeRentDetails = Array.isArray(rentInvoiceDetails) ? rentInvoiceDetails.map(item => ({
    ItemName: item?.ItemName || `Item ${item?.ItemId || 'Unknown'}`,
    FromDate: item?.FromDate || '',
    ToDate: item?.ToDate || '',
    NoOfDays: parseFloat(item?.NoOfDays) || 0,
    Quantity: parseFloat(item?.Quantity) || 0,
    Rate: parseFloat(item?.Rate) || 0,
    Amount: parseFloat(item?.Amount) || 0,
  })) : [];

  // Helper functions
  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return `${numAmount.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (error) {
      console.warn('Date formatting error:', error);
      return '-';
    }
  };

  const getPaymentStatus = () => {
    const balance = safeRentData.BalanceAmount;
    const total = safeRentData.TotalAmount;
    
    if (balance <= 0) return { status: 'PAID', color: '#10b981' };
    if (balance === total) return { status: 'UNPAID', color: '#ef4444' };
    return { status: 'PARTIAL', color: '#f59e0b' };
  };

  const paymentStatus = getPaymentStatus();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Watermark */}
        <Text style={styles.watermark}>RENT INVOICE</Text>
        
        {/* Status Badge */}
        <View style={[
          styles.statusBadge,
          paymentStatus.status === 'PARTIAL' && styles.statusBadgePending,
          paymentStatus.status === 'UNPAID' && styles.statusBadgeOverdue
        ]}>
          <Text>{paymentStatus.status}</Text>
        </View>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companySection}>
            <Text style={styles.companyName}>
              {companyData?.name || 'Your Company Name'}
            </Text>
            <Text style={styles.companyDetails}>
              {companyData?.address || 'Company Address Line 1'}{'\n'}
              {companyData?.city || 'City'}, {companyData?.state || 'State'} - {companyData?.pincode || '000000'}{'\n'}
              Phone: {companyData?.phone || '+91 00000 00000'} | Email: {companyData?.email || 'info@company.com'}{'\n'}
              {companyData?.gst && `GST: ${companyData.gst}`} | {companyData?.pan && `PAN: ${companyData.pan}`}
            </Text>
          </View>
          
          <View style={styles.invoiceSection}>
            <Text style={styles.invoiceTitle}>RENT INVOICE</Text>
            <Text style={styles.invoiceNumber}>
              #{safeRentData.BillNumber}
            </Text>
            <Text style={styles.invoiceDate}>
              Date: {formatDate(safeRentData.BillDate)}
            </Text>
          </View>
        </View>

        {/* Customer Section */}
        <View style={styles.customerSection}>
          <View style={styles.billToSection}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={styles.customerName}>
              {safeRentData.CustomerName}
            </Text>
            <Text style={styles.customerDetails}>
              {safeRentData.AddressLine1 && `${safeRentData.AddressLine1}\n`}
              {safeRentData.AddressLine2 && `${safeRentData.AddressLine2}\n`}
              {safeRentData.Location && `${safeRentData.Location}, `}
              {safeRentData.PinCode && `${safeRentData.PinCode}\n`}
              {safeRentData.ContactNumber1 && `Phone: ${safeRentData.ContactNumber1}`}
              {safeRentData.ContactNumber2 && ` | ${safeRentData.ContactNumber2}`}
            </Text>
          </View>
          
          <View style={styles.billToSection}>
            <Text style={styles.sectionTitle}>Invoice Details</Text>
            <Text style={styles.customerDetails}>
              Invoice Date: {formatDate(safeRentData.BillDate)}{'\n'}
              Invoice Type: Rent Invoice{'\n'}
              {safeRentData.Remarks && `Remarks: ${safeRentData.Remarks}`}
            </Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.col1]}>S.No</Text>
            <Text style={[styles.tableHeaderText, styles.col2, styles.tableCellLeft]}>Item Description</Text>
            <Text style={[styles.tableHeaderText, styles.col3]}>From Date</Text>
            <Text style={[styles.tableHeaderText, styles.col4]}>To Date</Text>
            <Text style={[styles.tableHeaderText, styles.col5]}>Days</Text>
            <Text style={[styles.tableHeaderText, styles.col6]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.col7]}>Rate</Text>
            <Text style={[styles.tableHeaderText, styles.col8]}>Amount</Text>
          </View>

          {/* Table Rows */}
          {safeRentDetails.map((item, index) => (
            <View key={index} style={[
              styles.tableRow,
              index % 2 === 1 && styles.tableRowAlt
            ]}>
              <Text style={[styles.tableCell, styles.col1]}>{index + 1}</Text>
              <Text style={[styles.tableCell, styles.col2, styles.tableCellLeft]}>
                {item.ItemName}
              </Text>
              <Text style={[styles.tableCell, styles.col3]}>{formatDate(safeRentData.DeliveryOutDate)}</Text>
              <Text style={[styles.tableCell, styles.col4]}>{formatDate(safeRentData.DeliveryInCreatedDate)}</Text>
              <Text style={[styles.tableCell, styles.col5]}>{safeRentData.EventDays}</Text>
              <Text style={[styles.tableCell, styles.col6]}>{item.Quantity}</Text>
              <Text style={[styles.tableCell, styles.col7, styles.tableCellRight]}>
                {formatCurrency(item.Rate)}
              </Text>
              <Text style={[styles.tableCell, styles.col8, styles.tableCellRight]}>
                {formatCurrency(item.Amount)}
              </Text>
            </View>
          ))}
        </View>

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryLeft}>
            {/* Payment Information */}
            <View style={styles.paymentSection}>
              <Text style={styles.paymentTitle}>Payment Information</Text>
              <Text style={styles.paymentDetails}>
                Total Amount: {formatCurrency(safeRentData.TotalAmount)}{'\n'}
                Paid Amount: {formatCurrency(safeRentData.PaidAmount)}{'\n'}
                Advance Amount: {formatCurrency(safeRentData.AdvanceAmount)}{'\n'}
                Balance Amount: {formatCurrency(safeRentData.BalanceAmount)}{'\n'}
                Status: {paymentStatus.status}
              </Text>
            </View>
          </View>

          <View style={styles.summaryRight}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Amount:</Text>
              <Text style={styles.totalValue}>{formatCurrency(safeRentData.TotalAmount)}</Text>
            </View>
            
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Paid Amount:</Text>
              <Text style={styles.summaryValue}>{formatCurrency(safeRentData.PaidAmount)}</Text>
            </View>
            
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Advance Amount:</Text>
              <Text style={styles.summaryValue}>{formatCurrency(safeRentData.AdvanceAmount)}</Text>
            </View>
            
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Balance Amount:</Text>
              <Text style={[styles.summaryValue, { color: safeRentData.BalanceAmount > 0 ? '#dc2626' : '#10b981' }]}>
                {formatCurrency(safeRentData.BalanceAmount)}
              </Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.footerSection}>
            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Terms & Conditions</Text>
              <Text style={styles.footerText}>
                1. Payment is due within 30 days of invoice date.{'\n'}
                2. Late payments may incur additional charges.{'\n'}
                3. Items once rented must be returned in good condition.{'\n'}
                4. Subject to local jurisdiction only.
              </Text>
            </View>
            
            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Bank Details</Text>
              <Text style={styles.footerText}>
                Bank Name: {companyData?.bankName || 'Your Bank Name'}{'\n'}
                Account No: {companyData?.accountNo || 'XXXX XXXX XXXX'}{'\n'}
                IFSC Code: {companyData?.ifscCode || 'XXXX0000XXX'}{'\n'}
                Branch: {companyData?.branch || 'Branch Name'}
              </Text>
            </View>
            
            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Contact Information</Text>
              <Text style={styles.footerText}>
                Email: {companyData?.email || 'info@company.com'}{'\n'}
                Phone: {companyData?.phone || '+91 00000 00000'}{'\n'}
              </Text>
            </View>
          </View>
          
          {/* Signature */}
          <View style={styles.signature}>
            <Text style={styles.signatureText}>For {companyData?.name || 'Your Company Name'}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Authorized Signatory</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default RentInvoicePDF;
